import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { api, SERVER_ORIGIN } from '../services/api';
import { shrinkPhoto } from '../utils/image';
import { isRetryableUploadError } from '../hooks/useOfflinePhotoQueue';

/**
 * Photos on a crew note — the gate latch, the dog door, the sprinkler head
 * to steer around. 'this' rides with one visit; 'future' hangs on the
 * property and shows on every later visit there, like the standing note.
 *
 * The add button opens the phone's own chooser — take a photo, or pick one
 * already in the photo library (owner, Oct 29 2026: "allow me to add a photo
 * of a photo I already have … from my photo library"). The input carries no
 * capture attribute on purpose: with capture="environment" an iPhone opens
 * the camera and nothing else. The crew's job photos and the winter-upsell
 * camera keep theirs; a note photo is as often one taken earlier.
 */
export type NotePhoto = { id: number; url: string; scope: 'this' | 'future'; caption?: string | null; mine?: boolean; user_id?: number | null };
export type NoteScope = 'this' | 'future';
export type EnqueueOffline = (file: File, opts: { kind: 'note'; scope: NoteScope }, force?: boolean) => Promise<boolean>;

export function NotePhotoStrip({ photos, canRemove, onRemoved, size = 'md' }: {
  photos: NotePhoto[] | null | undefined;
  canRemove?: boolean | ((p: NotePhoto) => boolean);
  onRemoved?: () => void;
  size?: 'sm' | 'md';
}) {
  const [open, setOpen] = useState<NotePhoto | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  if (!photos || photos.length === 0) return null;
  const allowed = (p: NotePhoto) => (typeof canRemove === 'function' ? canRemove(p) : !!canRemove);
  async function remove(p: NotePhoto) {
    if (!window.confirm('Remove this photo from the note?')) return;
    setBusy(p.id);
    try { await api.delete(`/jobs/crew-note-photos/${p.id}`); toast.success('Photo removed'); onRemoved?.(); }
    catch (e: any) { toast.error(e.response?.data?.message || 'Could not remove the photo'); }
    finally { setBusy(null); }
  }
  const box = size === 'sm' ? 'h-14 w-14' : 'h-20 w-20';
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {photos.map(p => (
          <div key={p.id} className={`relative ${box} flex-shrink-0`}>
            <button type="button" onClick={e => { e.stopPropagation(); setOpen(p); }} className="block h-full w-full">
              <img src={`${SERVER_ORIGIN}${p.url}`} alt={p.caption || 'Crew note photo'} className="h-full w-full object-cover rounded-lg border border-amber-700/30 bg-white" />
            </button>
            {p.scope === 'future' && <span className="absolute bottom-0.5 left-0.5 text-[9px] leading-none px-1 py-0.5 rounded bg-amber-950/85 text-amber-100">every visit</span>}
            {allowed(p) && (
              <button type="button" onClick={e => { e.stopPropagation(); remove(p); }} disabled={busy === p.id} title="Remove"
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-gray-900 text-white text-xs leading-none flex items-center justify-center shadow">×</button>
            )}
          </div>
        ))}
      </div>
      {open && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-3" onClick={() => setOpen(null)}>
          <img src={`${SERVER_ORIGIN}${open.url}`} alt={open.caption || 'Crew note photo'} className="max-h-[80vh] max-w-full object-contain rounded-lg" />
          <p className="text-snow text-sm mt-3 text-center">{open.caption ? `${open.caption} · ` : ''}{open.scope === 'future' ? 'shows on every visit' : 'this visit only'}</p>
          <p className="text-gray-500 text-xs mt-1">Tap anywhere to close</p>
        </div>
      )}
    </>
  );
}

export function AddNotePhotoButton({ jobId, scope, label, onAdded, enqueueOffline, disabled, className }: {
  jobId: number;
  scope: NoteScope;
  label: string;
  onAdded?: () => void;
  enqueueOffline?: EnqueueOffline;
  disabled?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    let small: File = file;
    try {
      small = await shrinkPhoto(file);
      if (enqueueOffline && await enqueueOffline(small, { kind: 'note', scope })) return;   // saved on the phone; uploads later
      if (!navigator.onLine) { toast.error('No signal — try the photo again when you are back in range'); return; }
      const form = new FormData();
      form.append('photo', small);
      form.append('scope', scope);
      const r = await api.post(`/jobs/${jobId}/crew-notes/photos`, form);
      toast.success(r.data?.message || 'Photo saved');
      onAdded?.();
    } catch (err: any) {
      // Weak signal dropped the upload: keep the photo on the phone and let the
      // queue send it, instead of making the crew take it again.
      if (isRetryableUploadError(err) && enqueueOffline && await enqueueOffline(small, { kind: 'note', scope }, true)) return;
      toast.error(err.response?.data?.message || 'Could not save the photo');
    } finally { setBusy(false); }
  }
  return (
    <>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
      <button type="button" disabled={disabled || busy} onClick={e => { e.stopPropagation(); ref.current?.click(); }}
        className={className || 'text-xs px-2.5 py-1.5 rounded-md border border-amber-500 bg-white text-amber-900 font-semibold disabled:opacity-50'}>
        📷 {busy ? 'Saving…' : label}
      </button>
    </>
  );
}
